'use client';

import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { PDFDocument } from 'pdf-lib';
import Icon from '@/components/ui/AppIcon';
import { useToast } from '@/components/ui/Toast';
import { isEncryptedPDF } from '@/utils/pdf';
import UploadZone from '@/components/pdf/UploadZone';
import Script from 'next/script';
import PDFViewerModal from '@/components/pdf/PDFViewerModal';
import { ensurePdfWorker, getPdfjsLib, renderPageThumbnail } from '@/utils/pdfThumbnailUtils';
import { FileCard } from '@/components/pdf/FileCard';
import { getPdfMeta, useEncryptedPdfToast } from '@/constants/pdfConfig';

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type?: string;
  file: File;
  pageCount: number;
}

interface PageThumbnail {
  id: string;
  pageIndex: number;
  url: string;
  deleted?: boolean;
}

const MAX_SIZE = 100 * 1024 * 1024; // 100MB

const REMOVE_PAGE_THEME = {
  primary: '#EF4444',
  primaryLight: '#FEF2F2',
  primaryBorder: '#FECACA',
  primaryShadow: 'rgba(239, 68, 68, 0.28)',
};

export default function RemovePagesPDFUploader() {
  const [file, setFile] = useState<UploadedFile | null>(null);
  const [thumbnails, setThumbnails] = useState<PageThumbnail[]>([]);
  const [isDone, setIsDone] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [processedPdfBlob, setProcessedPdfBlob] = useState<Blob | null>(null);
  // Single source of truth for which pages are marked for deletion.
  // Using a Set so it works even before thumbnails have loaded.
  const [deletedPages, setDeletedPages] = useState<Set<number>>(new Set());

  const { showToast } = useToast();
  const { showEncryptedPdfToast } = useEncryptedPdfToast();

  /** Handle File Upload (Single File Only) */
  const handleFiles = useCallback(
    async (newFiles: FileList | null) => {
      if (!newFiles || newFiles.length === 0) return;

      const f = newFiles[0];

      if (
        !(f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf')) ||
        f.size > MAX_SIZE
      ) {
        showToast('Invalid file type or file too large (max 100MB).', 'error');
        return;
      }

      try {
        // Pre-flight: check for encryption
        const alreadyEncrypted = await isEncryptedPDF(f);
        if (alreadyEncrypted) {
          showEncryptedPdfToast('removing pages');
          return;
        }

        // Cleanup old thumbnails and reset deletion state
        thumbnails.forEach((t) => URL.revokeObjectURL(t.url));
        setThumbnails([]);
        setDeletedPages(new Set());

        const meta = await getPdfMeta(f);
        setFile(meta);
        setIsDone(false);
      } catch (err) {
        console.error('Error reading PDF:', err);
        alert('Failed to read PDF file.');
      }
    },
    [thumbnails]
  );

  /** Generate Thumbnails */

  /** Lazy thumbnail generator effect */
  useEffect(() => {
    if (!file) return;

    let cancelled = false;
    const loadedThumbs: PageThumbnail[] = [];

    const generateThumbnailsLazy = async () => {
      try {
        const bytes = await file.file.arrayBuffer();
        const pdfjsLib = getPdfjsLib();
        if (!pdfjsLib) return;

        ensurePdfWorker(pdfjsLib);

        const loadingTask = pdfjsLib.getDocument({ data: bytes });
        const pdf = await loadingTask.promise;

        for (let i = 1; i <= pdf.numPages; i++) {
          if (cancelled) break;

          const result = await renderPageThumbnail(pdf, i - 1, { scale: 0.5 });
          if (!result) continue;

          const thumb: PageThumbnail = {
            id: crypto.randomUUID(),
            pageIndex: i - 1,
            url: result.url,
          };
          loadedThumbs.push(thumb);

          // update thumbnails progressively
          setThumbnails([...loadedThumbs]);

          // optional small delay to prevent UI freeze
          await new Promise((r) => setTimeout(r, 10));
        }
      } catch (err) {
        console.error(err);
      }
    };

    generateThumbnailsLazy();

    return () => {
      cancelled = true;
      loadedThumbs.forEach((t) => URL.revokeObjectURL(t.url));
    };
  }, [file]);

  /** Mark / unmark a page for deletion (works before thumbnail has loaded) */
  const removePage = useCallback((pageIndex: number) => {
    setDeletedPages((prev) => new Set([...prev, pageIndex]));
  }, []);

  const restorePage = useCallback((pageIndex: number) => {
    setDeletedPages((prev) => {
      const next = new Set(prev);
      next.delete(pageIndex);
      return next;
    });
  }, []);

  /** Remove Entire File */
  const removeFile = useCallback(() => {
    thumbnails.forEach((t) => URL.revokeObjectURL(t.url));
    setThumbnails([]);
    setDeletedPages(new Set());
    setFile(null);
    setIsDone(false);
    setProcessedPdfBlob(null);
  }, [thumbnails]);

  /** Generate Final PDF */
  const handleRemovePages = useCallback(async () => {
    if (!file) return;

    const mergedPdf = await PDFDocument.create();
    const bytes = await file.file.arrayBuffer();
    const pdf = await PDFDocument.load(bytes);

    // Build list of page indices to keep (0-based), using deletedPages as
    // the authoritative source so pre-deletion of loading pages is respected.
    const pagesToKeep = Array.from({ length: file.pageCount }, (_, i) => i).filter(
      (i) => !deletedPages.has(i)
    );

    if (pagesToKeep.length === 0) {
      alert('No pages selected to keep.');
      return;
    }

    const copiedPages = await mergedPdf.copyPages(pdf, pagesToKeep);
    copiedPages.forEach((page) => mergedPdf.addPage(page));

    const finalBytes: any = await mergedPdf.save();
    const blob = new Blob([finalBytes], { type: 'application/pdf' });

    setProcessedPdfBlob(blob);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'modified.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    setTimeout(() => URL.revokeObjectURL(url), 100);
    setIsDone(true);
  }, [file, thumbnails, deletedPages]);

  const handleDownload = useCallback(() => {
    if (!processedPdfBlob) return;

    const url = URL.createObjectURL(processedPdfBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `modified_${file?.name || 'pdf'}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }, [processedPdfBlob, file]);

  const thumbnailMap = useMemo(() => {
    const map = new Map();
    thumbnails.forEach((t) => map.set(t.pageIndex, t));
    return map;
  }, [thumbnails]);

  return (
    <div className="w-full max-w-6xl mx-auto px-4">
      <Script
        src="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.10.38/pdf.min.mjs"
        type="module"
        strategy="afterInteractive"
      />

      {file && (
        <PDFViewerModal
          file={file}
          isOpen={viewerOpen}
          currentPage={currentPage}
          onClose={() => setViewerOpen(false)}
          onPageChange={setCurrentPage}
          onDelete={removePage}
          onRestore={restorePage}
          isPageDeleted={(i) => deletedPages.has(i)}
        />
      )}

      {/* Upload Zone */}
      {!file && (
        <UploadZone
          onFilesSelected={handleFiles}
          multiple={false}
          accentColor="#E8445A"
          iconName="TrashIcon"
          title="Drop PDF file here"
          subtitle="or click to browse — single file only"
          buttonText="Select PDF File"
          dragTitle="Drop your PDF here!"
        />
      )}

      {/* ================= MAIN WORK AREA ================= */}
      {file && !isDone && (
        <div className="flex flex-col lg:flex-row gap-6 mt-4">
          {/* ================= LEFT SIDE ================= */}
          <div className="flex-1 min-w-0">
            {/* Header */}
            <FileCard file={file} onRemove={removeFile} theme={REMOVE_PAGE_THEME} />

            {/* Scrollable Thumbnails */}
            <div className="max-h-[70vh] overflow-y-auto pr-2 pb-20 lg:pb-0">
              <div className="grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {Array.from({ length: file.pageCount }).map((_, i) => {
                  const thumb = thumbnailMap.get(i);
                  const isDeleted = deletedPages.has(i);

                  if (!thumb) {
                    return (
                      <div
                        key={i}
                        className="relative rounded-xl animate-pulse aspect-[100/140] bg-brand-surface"
                      >
                        <span className="absolute bottom-1 left-0 right-0 text-center text-[10px] text-brand-muted">
                          {i + 1}
                        </span>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                            isDeleted ? restorePage(i) : removePage(i);
                          }}
                          className="absolute top-2 right-2 z-30 w-6 h-6 rounded-full flex items-center justify-center text-white text-[12px]"
                          style={{
                            background: isDeleted ? '#16A34A' : '#E8445A',
                          }}
                        >
                          {isDeleted ? '↺' : '×'}
                        </button>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={thumb.id}
                      className="relative border rounded-xl overflow-hidden aspect-[100/140] group cursor-pointer transition"
                      style={{ opacity: isDeleted ? 0.4 : 1 }}
                      onClick={() => {
                        setCurrentPage(i);
                        setViewerOpen(true);
                      }}
                    >
                      <img
                        src={thumb.url}
                        alt={`Page ${i + 1}`}
                        className="w-full h-full object-cover"
                      />

                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                        <Icon name="EyeIcon" size={20} className="text-white" />
                      </div>

                      {/* Deleted overlay */}
                      {isDeleted && (
                        <div className="absolute inset-0 bg-red-500/20 flex items-center justify-center">
                          <span className="text-[12px] font-bold text-primary bg-white px-1.5 rounded-md">
                            Deleted
                          </span>
                        </div>
                      )}

                      {/* Action Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                          isDeleted ? restorePage(i) : removePage(i);
                        }}
                        className="absolute top-2 right-2 z-30 w-6 h-6 rounded-full flex items-center justify-center text-white text-[12px]"
                        style={{
                          background: isDeleted ? '#16A34A' : '#E8445A',
                        }}
                      >
                        <Icon name={isDeleted ? 'PlusIcon' : 'XMarkIcon'} size={12} />
                      </button>

                      <span className="absolute bottom-1 left-0 right-0 text-center text-[10px] text-brand-muted">
                        {i + 1}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ================= RIGHT SIDEBAR ================= */}
          <div className="w-full lg:w-[280px]">
            <div className="lg:sticky lg:top-6 space-y-4">
              <div className="p-3 rounded-xl border border-brand-border bg-brand-surface">
                <div className="grid grid-cols-3 gap-y-2 text-[11px] text-brand-muted">
                  <span className="flex items-center gap-1">
                    <Icon name="XMarkIcon" size={13} />
                    Delete
                  </span>

                  <span className="flex items-center gap-1">
                    <Icon name="PlusIcon" size={13} />
                    Restore
                  </span>

                  <span className="flex items-center gap-1">
                    <Icon name="EyeIcon" size={13} />
                    Preview
                  </span>
                </div>
              </div>

              {/* Stats (Your Improved Version) */}
              <div className="grid grid-cols-3 gap-2 p-4 rounded-2xl bg-brand-surface border border-brand-border text-center">
                <div>
                  <p className="text-xs text-brand-muted mb-1 font-body">Total</p>
                  <p className="text-lg font-bold text-brand-dark font-heading">{file.pageCount}</p>
                </div>

                <div className="border-x border-brand-border">
                  <p className="text-xs text-brand-muted mb-1 font-body">Remove</p>
                  <p className="text-lg font-bold text-primary font-heading">{deletedPages.size}</p>
                </div>

                <div>
                  <p className="text-xs text-brand-muted mb-1 font-body">Final</p>
                  <p className="text-lg font-bold text-green-600 font-heading">
                    {file.pageCount - deletedPages.size}
                  </p>
                </div>
              </div>

              {/* Primary Action */}
              <button
                onClick={handleRemovePages}
                disabled={deletedPages.size >= file.pageCount}
                className="w-full py-4 rounded-2xl font-heading font-bold text-base text-white disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: deletedPages.size < file.pageCount ? '#E8445A' : '#ccc',
                  boxShadow:
                    deletedPages.size < file.pageCount ? '0 6px 20px rgba(232,68,90,0.28)' : 'none',
                }}
              >
                Download Updated PDF
              </button>

              {/* Undo All (Secondary Action — No Layout Shift) */}
              <button
                onClick={() => setDeletedPages(new Set())}
                disabled={deletedPages.size === 0}
                className="w-full py-2 text-sm font-semibold rounded-xl border border-brand-border text-brand-mid disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Undo All Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= DONE STATE ================= */}
      {isDone && (
        <div className="text-center py-8">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 pulse-anim bg-green-50">
            <Icon name="CheckBadgeIcon" size={42} variant="solid" className="text-green-600" />
          </div>

          <h3 className="font-heading font-bold mb-2 text-[20px] text-brand-dark">
            Pages Removed Successfully!
          </h3>

          <p className="text-brand-mid text-[14px] font-body mb-6">
            Selected pages have been removed from your PDF.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <button
              className="sm:w-auto flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary text-white border-none font-heading font-bold text-[15px] cursor-pointer shadow-[0_6px_20px_rgba(232,68,90,0.28)]"
              onClick={handleDownload}
            >
              <Icon name="ArrowDownTrayIcon" size={18} variant="solid" />
              Download Updated PDF
            </button>

            <button
              onClick={removeFile}
              className="px-8 py-4 rounded-full border-2 border-brand-border font-heading font-bold text-brand-mid hover:bg-brand-surface transition-all active:scale-95 flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3B82F6] focus-visible:ring-offset-2"
            >
              <Icon name="ArrowPathIcon" size={18} />
              Edit Another PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
